import type { BottleDeparture, BottleSummary, StorageSummary } from "@corklog/shared";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useMayCarry, useMayWrite } from "../cellar.js";
import { actionButton, control, fieldLabel, resting } from "./controls.js";
import {
  ApiError,
  consumeBottle,
  departBottle,
  moveBottles,
  returnBottle,
  revertBottleEvent,
  takeBottleAway,
} from "../api.js";

/**
 * Where this wine's bottles are, and everything that can happen to one.
 *
 * Bottles are grouped rather than listed one by one, because within a lot they
 * are indistinguishable: six bottles of the same wine on the same shelf are
 * six identical objects, and a list of six rows with nothing to tell them
 * apart would be six ways to pick the same thing. So the interface deals in
 * counts — *three of the six on Lagring hylla 2* — and resolves that to actual
 * bottle ids before it asks the API, because the history has to record which
 * objects moved even when the person moving them had no reason to care.
 *
 * Two groupings, because there are two kinds of place a bottle owned by this
 * cellar can be. Standing bottles group by cabinet, shelf and format. Bottles
 * that are out group by where they went, since that is the only thing telling
 * two absent bottles apart — three taken to the same dinner are one row, and
 * one at the office is another.
 *
 * Capacity warns and does not refuse, per project-scope.md. The shelf's new
 * occupancy comes back with the write, and an overfull shelf is said out loud
 * afterwards rather than being turned into a refusal — the cabinet is the
 * authority on what fits in it, not the record.
 */
type Group = {
  readonly key: string;
  readonly storageName: string;
  readonly shelfName: string;
  readonly format: BottleSummary["format"];
  readonly bottleIds: readonly string[];
};

/** A group of bottles that are out of the cellar and coming back. */
type AwayGroup = Group & {
  readonly note: string | null;
  /** Null once the shelf they left has been removed. Then returning asks. */
  readonly backToShelf: string | null;
  readonly backToStorage: string | null;
  /**
   * Who carried them out and on what day. Null for an act the import
   * performed or whose account has gone, and null on an older API.
   */
  readonly by: string | null;
  readonly on: string | null;
};

/**
 * The four ways a bottle leaves for good, deliberate first and accidental
 * last. Bortgiven leads because it is the one that happens on purpose and the
 * one most often recorded.
 */
const DEPARTURES: readonly BottleDeparture[] = ["Gifted", "Sold", "Broken", "Lost"];

/** Standing bottles, gathered by where they are and what size they are. */
function groupBottles(bottles: readonly BottleSummary[]): Group[] {
  const groups = new Map<string, Group>();
  for (const bottle of bottles) {
    if (bottle.status !== "InCellar") continue;
    const storageName = bottle.storageName ?? "—";
    const shelfName = bottle.shelfName ?? "—";
    // JSON rather than a joined string: a shelf can be called anything, and a
    // separator that may itself appear inside a name would merge two places
    // into one row. The place key is never rendered — a group is identified to
    // React and to the DOM by one of its own bottle ids.
    const place = JSON.stringify([storageName, shelfName, bottle.format]);
    const found = groups.get(place);
    groups.set(place, {
      key: found?.key ?? bottle.id,
      storageName,
      shelfName,
      format: bottle.format,
      bottleIds: [...(found?.bottleIds ?? []), bottle.id],
    });
  }
  return [...groups.values()];
}

/**
 * Bottles that are out, gathered by the trip they are on.
 *
 * The note is part of the key rather than shown from the first bottle of the
 * group: two bottles away for different reasons are two facts, and folding
 * them into one row would attribute one bottle's errand to the other.
 */
function groupAway(bottles: readonly BottleSummary[]): AwayGroup[] {
  const groups = new Map<string, AwayGroup>();
  for (const bottle of bottles) {
    if (bottle.status !== "Away") continue;
    // Who took them and when are part of the key, like the note: two bottles
    // out on the same errand but carried by different people on different days
    // are two facts, and folding them into one row would put one person's name
    // on the other's bottle.
    const trip = JSON.stringify([
      bottle.awayNote,
      bottle.awayFromStorageName,
      bottle.awayFromShelfName,
      bottle.format,
      bottle.awayBy,
      bottle.awayOn,
    ]);
    const found = groups.get(trip);
    groups.set(trip, {
      key: found?.key ?? bottle.id,
      storageName: bottle.awayFromStorageName ?? "—",
      shelfName: bottle.awayFromShelfName ?? "—",
      format: bottle.format,
      note: bottle.awayNote,
      backToShelf: bottle.awayFromShelfName,
      backToStorage: bottle.awayFromStorageName,
      // The `?? null` these two carried was the additive two-step's, folding
      // *the API did not say* into *there is nobody to name*. The contract
      // refuses the first outright as of 2026-09-22, so the null that arrives
      // here is the real one: a bottle the import carried out, or one whose
      // account has gone.
      by: bottle.awayBy,
      on: bottle.awayOn,
      bottleIds: [...(found?.bottleIds ?? []), bottle.id],
    });
  }
  return [...groups.values()];
}

/** Every shelf in the cellar, each carrying the cabinet it belongs to. */
function shelvesOf(storages: readonly StorageSummary[]) {
  return storages.flatMap((storage) =>
    storage.shelves.map((shelf) => ({ ...shelf, storageName: storage.name })),
  );
}

function MoveForm({
  cellarId,
  group,
  storages,
  onDone,
  onCancel,
}: {
  cellarId: string;
  group: Group;
  storages: readonly StorageSummary[];
  onDone: (notice: string) => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const shelves = shelvesOf(storages);

  const [count, setCount] = useState(String(group.bottleIds.length));
  const [shelfId, setShelfId] = useState(shelves[0]?.id ?? "");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const wanted = Number(count);
  const canSubmit =
    shelfId !== "" &&
    Number.isInteger(wanted) &&
    wanted > 0 &&
    wanted <= group.bottleIds.length &&
    !busy;

  return (
    <form
      className="mt-3 flex flex-wrap items-end gap-4"
      onSubmit={(event) => {
        event.preventDefault();
        if (!canSubmit) return;
        setBusy(true);
        setError(null);
        const shelf = shelves.find((candidate) => candidate.id === shelfId);
        void moveBottles(cellarId, {
          // The first n of the group. Which ones is arbitrary because they are
          // identical, but it is decided here and sent explicitly rather than
          // left for the server to choose, so the event log names real bottles.
          bottleIds: group.bottleIds.slice(0, wanted),
          toShelfId: shelfId,
        })
          .then((result) => {
            const moved = t("wine.moved", { count: result.moved });
            const full =
              result.shelfCapacity !== null && result.shelfCount > result.shelfCapacity
                ? ` ${t("wine.overCapacity", {
                    shelf: shelf?.name ?? "",
                    capacity: result.shelfCapacity,
                    count: result.shelfCount,
                  })}`
                : "";
            onDone(`${moved}${full}`);
          })
          .catch(() => setError(t("wine.moveFailed")))
          .finally(() => setBusy(false));
      }}
    >
      <div>
        <label className={fieldLabel} htmlFor={`move-count-${group.key}`}>
          {t("wine.moveCount")}
        </label>
        <input
          id={`move-count-${group.key}`}
          inputMode="numeric"
          value={count}
          onChange={(event) => setCount(event.target.value)}
          className={`${control} ${resting} mt-1`}
        />
      </div>

      <div>
        <label className={fieldLabel} htmlFor={`move-shelf-${group.key}`}>
          {t("wine.moveTo")}
        </label>
        <select
          id={`move-shelf-${group.key}`}
          value={shelfId}
          onChange={(event) => setShelfId(event.target.value)}
          className={`${control} ${resting} mt-1`}
        >
          {shelves.map((shelf) => (
            <option key={shelf.id} value={shelf.id}>
              {shelf.storageName} · {shelf.name}
            </option>
          ))}
        </select>
      </div>

      <button type="submit" disabled={!canSubmit} className={actionButton}>
        {busy ? t("wine.saving") : t("wine.moveAction")}
      </button>
      <button
        type="button"
        onClick={onCancel}
        className="text-muted text-sm underline-offset-4 hover:underline"
      >
        {t("wines.cancel")}
      </button>
      {error !== null && (
        <span role="alert" className="text-past text-sm">
          {error}
        </span>
      )}
    </form>
  );
}

/**
 * Drinking one bottle out of a group: the date, and what it tasted like.
 *
 * Two taps plus the note, per the plan, and the form is shaped by that count.
 * The date is already today, the note has focus, and the only other control is
 * the one that says it happened — a bottle is opened standing in a kitchen,
 * not filled in at a desk. Everything else that could be asked here is asked
 * somewhere else or not at all.
 *
 * There is no score here. 3.10 found that the judgement being made when a
 * bottle is opened is about the wine rather than about that bottle — the same
 * 17 typed onto three identical bottles — so the score box lives on the wine.
 * What belongs here is what was true of this bottle on this evening: the
 * words, and whether it was corked.
 */
function DrinkForm({
  cellarId,
  group,
  onDone,
  onCancel,
}: {
  cellarId: string;
  group: Group;
  onDone: (said: string, eventId: string) => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const today = new Date().toISOString().slice(0, 10);
  const [drunkOn, setDrunkOn] = useState(today);
  const [note, setNote] = useState("");
  const [isFaulty, setFaulty] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  return (
    <form
      className="mt-3"
      onSubmit={(event) => {
        event.preventDefault();
        if (busy || drunkOn === "") return;
        setBusy(true);
        setError(null);
        // The first of the group. Which bottle is arbitrary because they are
        // identical, but it is decided here and named explicitly, so the
        // history records an object rather than a quantity.
        const bottleId = group.bottleIds[0] ?? "";
        // No score here. 3.10 found that the judgement being made is about the
        // wine rather than the bottle — the same 17 typed three times onto
        // three identical bottles — so the score box lives on the wine now.
        // TastingNote.score remains in the model for a bottle that genuinely
        // differed from its siblings; nothing writes it yet.
        void consumeBottle(cellarId, bottleId, { drunkOn, note, isFaulty, score: null })
          .then((result) => onDone(t("wine.drunk"), result.eventId))
          .catch(() => setError(t("wine.drinkFailed")))
          .finally(() => setBusy(false));
      }}
    >
      <div className="flex flex-wrap items-end gap-4">
        <div>
          <label className={fieldLabel} htmlFor={`drink-date-${group.key}`}>
            {t("wine.drunkOn")}
          </label>
          <input
            id={`drink-date-${group.key}`}
            type="date"
            value={drunkOn}
            onChange={(event) => setDrunkOn(event.target.value)}
            className={`${control} ${resting} mt-1`}
          />
        </div>

        <label className="flex items-baseline gap-2 text-sm">
          <input
            type="checkbox"
            checked={isFaulty}
            onChange={(event) => setFaulty(event.target.checked)}
          />
          {t("wine.faulty")}
        </label>
      </div>

      <div className="mt-3">
        <label className={fieldLabel} htmlFor={`drink-note-${group.key}`}>
          {t("wine.tastingNote")}
        </label>
        <textarea
          id={`drink-note-${group.key}`}
          value={note}
          rows={2}
          // Focused, because the note is the one thing only the person who just
          // drank it can supply.
          autoFocus
          onChange={(event) => setNote(event.target.value)}
          className={`${control} ${resting} mt-1`}
        />
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-4">
        <button type="submit" disabled={busy || drunkOn === ""} className={actionButton}>
          {busy ? t("wine.saving") : t("wine.drink")}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="text-muted text-sm underline-offset-4 hover:underline"
        >
          {t("wines.cancel")}
        </button>
        {error !== null && (
          <span role="alert" className="text-past text-sm">
            {error}
          </span>
        )}
      </div>
    </form>
  );
}

/**
 * Taking a bottle out of the cellar without giving it up.
 *
 * The date and a line about where it went, which is the field that makes the
 * status worth having: *hos Anna, middagen på lördag* is what turns a bottle
 * missing from a shelf into a bottle somebody can go and fetch. It is optional
 * all the same — a bottle in the car is still better recorded as out than left
 * standing on a shelf it is not on.
 */
function AwayForm({
  cellarId,
  group,
  onDone,
  onCancel,
}: {
  cellarId: string;
  group: Group;
  onDone: (said: string, eventId: string) => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const today = new Date().toISOString().slice(0, 10);
  const [takenOn, setTakenOn] = useState(today);
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  return (
    <form
      className="mt-3"
      onSubmit={(event) => {
        event.preventDefault();
        if (busy || takenOn === "") return;
        setBusy(true);
        setError(null);
        const bottleId = group.bottleIds[0] ?? "";
        void takeBottleAway(cellarId, bottleId, { takenOn, note })
          .then((result) => onDone(t("wine.takenOut"), result.eventId))
          .catch(() => setError(t("wine.takeOutFailed")))
          .finally(() => setBusy(false));
      }}
    >
      <div className="flex flex-wrap items-end gap-4">
        <div>
          <label className={fieldLabel} htmlFor={`away-date-${group.key}`}>
            {t("wine.takenOutOn")}
          </label>
          <input
            id={`away-date-${group.key}`}
            type="date"
            value={takenOn}
            onChange={(event) => setTakenOn(event.target.value)}
            className={`${control} ${resting} mt-1`}
          />
        </div>

        <div className="min-w-48 flex-1">
          <label className={fieldLabel} htmlFor={`away-note-${group.key}`}>
            {t("wine.awayNote")}
          </label>
          <input
            id={`away-note-${group.key}`}
            value={note}
            autoFocus
            onChange={(event) => setNote(event.target.value)}
            className={`${control} ${resting} mt-1`}
          />
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-4">
        <button type="submit" disabled={busy || takenOn === ""} className={actionButton}>
          {busy ? t("wine.saving") : t("wine.takeOut")}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="text-muted text-sm underline-offset-4 hover:underline"
        >
          {t("wines.cancel")}
        </button>
        {error !== null && (
          <span role="alert" className="text-past text-sm">
            {error}
          </span>
        )}
      </div>
    </form>
  );
}

/**
 * A bottle that leaves for good: given away, sold, broken, lost.
 *
 * One form with the ending as its first control, rather than four buttons in a
 * row that is already carrying three. The ending is what is being recorded and
 * everything else about the act is the same, so it is the question the form
 * opens with — and the button carries the answer, so the word chosen is the
 * word pressed and then the word confirmed.
 *
 * Every ending takes a note, including the two nobody chose. *tappade den i
 * trappan* is worth as much in fifteen years as *till Anna, på dopet*, and a
 * field offered only for the flattering endings would be a judgement about
 * which losses deserve explaining.
 */
function DepartForm({
  cellarId,
  group,
  onDone,
  onCancel,
}: {
  cellarId: string;
  group: Group;
  onDone: (said: string, eventId: string) => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const today = new Date().toISOString().slice(0, 10);
  const [kind, setKind] = useState<BottleDeparture>("Gifted");
  const [leftOn, setLeftOn] = useState(today);
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  return (
    <form
      className="mt-3"
      onSubmit={(event) => {
        event.preventDefault();
        if (busy || leftOn === "") return;
        setBusy(true);
        setError(null);
        const bottleId = group.bottleIds[0] ?? "";
        void departBottle(cellarId, bottleId, { kind, leftOn, note })
          .then((result) => onDone(`${t(`departure.${kind}`)}.`, result.eventId))
          .catch(() => setError(t("wine.departFailed")))
          .finally(() => setBusy(false));
      }}
    >
      <div className="flex flex-wrap items-end gap-4">
        <div>
          <label className={fieldLabel} htmlFor={`depart-kind-${group.key}`}>
            {t("wine.departKind")}
          </label>
          <select
            id={`depart-kind-${group.key}`}
            value={kind}
            onChange={(event) => setKind(event.target.value as BottleDeparture)}
            className={`${control} ${resting} mt-1`}
          >
            {DEPARTURES.map((value) => (
              <option key={value} value={value}>
                {t(`departure.${value}`)}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className={fieldLabel} htmlFor={`depart-date-${group.key}`}>
            {t("wine.departedOn")}
          </label>
          <input
            id={`depart-date-${group.key}`}
            type="date"
            value={leftOn}
            onChange={(event) => setLeftOn(event.target.value)}
            className={`${control} ${resting} mt-1`}
          />
        </div>

        <div className="min-w-48 flex-1">
          <label className={fieldLabel} htmlFor={`depart-note-${group.key}`}>
            {t("wine.departNote")}
          </label>
          <input
            id={`depart-note-${group.key}`}
            value={note}
            onChange={(event) => setNote(event.target.value)}
            className={`${control} ${resting} mt-1`}
          />
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-4">
        <button type="submit" disabled={busy || leftOn === ""} className={actionButton}>
          {busy ? t("wine.saving") : t(`departure.${kind}`)}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="text-muted text-sm underline-offset-4 hover:underline"
        >
          {t("wines.cancel")}
        </button>
        {error !== null && (
          <span role="alert" className="text-past text-sm">
            {error}
          </span>
        )}
      </div>
    </form>
  );
}

/**
 * Putting a bottle back when the shelf it left is gone.
 *
 * Only rendered for that case. project-scope.md asks for returning to be one
 * action rather than a re-filing decision, so a bottle whose shelf still
 * exists never sees this form — it goes back where it came from on one tap.
 */
function ReturnForm({
  cellarId,
  group,
  storages,
  onDone,
  onCancel,
}: {
  cellarId: string;
  group: AwayGroup;
  storages: readonly StorageSummary[];
  onDone: (said: string) => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const shelves = shelvesOf(storages);
  const [shelfId, setShelfId] = useState(shelves[0]?.id ?? "");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  return (
    <form
      className="mt-3 flex flex-wrap items-end gap-4"
      onSubmit={(event) => {
        event.preventDefault();
        if (busy || shelfId === "") return;
        setBusy(true);
        setError(null);
        const bottleId = group.bottleIds[0] ?? "";
        void returnBottle(cellarId, bottleId, {
          returnedOn: new Date().toISOString().slice(0, 10),
          toShelfId: shelfId,
        })
          .then((result) =>
            onDone(
              t("wine.returned", {
                storage: result.storageName ?? "",
                shelf: result.shelfName ?? "",
              }),
            ),
          )
          .catch(() => setError(t("wine.returnFailed")))
          .finally(() => setBusy(false));
      }}
    >
      <div>
        <label className={fieldLabel} htmlFor={`return-shelf-${group.key}`}>
          {t("wine.returnTo")}
        </label>
        <select
          id={`return-shelf-${group.key}`}
          value={shelfId}
          onChange={(event) => setShelfId(event.target.value)}
          className={`${control} ${resting} mt-1`}
        >
          {shelves.map((shelf) => (
            <option key={shelf.id} value={shelf.id}>
              {shelf.storageName} · {shelf.name}
            </option>
          ))}
        </select>
      </div>

      <button type="submit" disabled={busy || shelfId === ""} className={actionButton}>
        {busy ? t("wine.saving") : t("wine.return")}
      </button>
      <button
        type="button"
        onClick={onCancel}
        className="text-muted text-sm underline-offset-4 hover:underline"
      >
        {t("wines.cancel")}
      </button>
      {error !== null && (
        <span role="alert" className="text-past text-sm">
          {error}
        </span>
      )}
    </form>
  );
}

type Acting = {
  key: string;
  kind: "move" | "drink" | "away" | "depart" | "return";
};

export function BottlePlacement({
  cellarId,
  bottles,
  storages,
  onMoved,
}: {
  cellarId: string;
  bottles: readonly BottleSummary[];
  storages: readonly StorageSummary[];
  /** Refetch: the group the bottles came from has just changed size. */
  onMoved: () => void;
}) {
  const { t } = useTranslation();
  const [acting, setActing] = useState<Acting | null>(null);
  // The notice carries the event that made it, when there is one. That is what
  // turns "Drucken." into something that can still be taken back.
  const [notice, setNotice] = useState<{ text: string; eventId: string | null } | null>(
    null,
  );
  const [undoing, setUndoing] = useState(false);
  const mayWrite = useMayWrite();
  const mayCarry = useMayCarry();

  const groups = groupBottles(bottles);
  const away = groupAway(bottles);
  const hasShelves = storages.some((storage) => storage.shelves.length > 0);

  const open = (key: string, kind: Acting["kind"]) => {
    setActing({ key, kind });
    setNotice(null);
  };

  const done = (said: string, eventId: string | null) => {
    setActing(null);
    setNotice({ text: said, eventId });
    onMoved();
  };

  /** One bottle of a group goes back where it came from, on one tap. */
  const goBack = (group: AwayGroup) => {
    setNotice(null);
    void returnBottle(cellarId, group.bottleIds[0] ?? "", {
      returnedOn: new Date().toISOString().slice(0, 10),
      toShelfId: null,
    })
      .then((result) =>
        done(
          t("wine.returned", {
            storage: result.storageName ?? "",
            shelf: result.shelfName ?? "",
          }),
          null,
        ),
      )
      .catch((error: unknown) => {
        // The shelf went while the page was open. That is the one case where
        // returning has to ask, so the form that asks is what appears.
        if (error instanceof ApiError && error.code === "shelf_required") {
          open(group.key, "return");
          return;
        }
        setNotice({ text: t("wine.returnFailed"), eventId: null });
      });
  };

  return (
    <>
      <ul className="mt-3 text-sm">
        {groups.map((group) => (
          <li key={group.key} className="border-line border-b py-2 last:border-b-0">
            {/* Where they are and how many, and nothing else on this line.
                The count is a fact about the row rather than one more thing to
                press, and while it shared a line with four actions its place
                on the screen moved with them — how far in the number sat
                depended on whether Flytta was offered and on how long the
                cabinet was called. A fact that moves is a fact that has to be
                looked for. */}
            <div className="flex items-baseline justify-between gap-4">
              <span>
                {group.storageName} · {group.shelfName} · {t(`format.${group.format}`)}
              </span>
              <span className="font-display">{group.bottleIds.length}</span>
            </div>

            {/* The four things that can happen to a bottle, on a line of their
                own. None of them is hidden behind a menu: the moment this page
                is for is standing in front of a cabinet, and drinking, lending,
                moving and writing off are all things done there. What was
                wrong was never that there are four — it was that four actions,
                a shelf name and a count were competing for one line on a
                phone.

                None of them is a guest's: where the bottles stand is what they
                came to read, and what happens to them is the cellar's own
                business.

                **The four split at 2026-09-21 and no longer stand or fall
                together.** Carrying a bottle out to a dinner is the husgäst's
                — that is what the role is for. The other three are the owner's:
                moving bottles between shelves is a filing decision about
                somebody else's cabinets, writing one off disposes of their
                property, and **drinking one is the cellar's stock**, which is
                the book the owner keeps. The husgäst carries the bottle to the
                dinner; the owner records that it is gone. */}
            {mayCarry === true && acting?.key !== group.key && (
              <div className="mt-1 flex flex-wrap items-baseline gap-x-5 gap-y-1">
                {/* Drinking first, and it is the one action that needs no
                    cabinet to exist. Moving needs somewhere to move to. */}
                {mayWrite === true && (
                  <button
                    type="button"
                    onClick={() => open(group.key, "drink")}
                    className="text-accent underline-offset-4 hover:underline"
                  >
                    {t("wine.drink")}
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => open(group.key, "away")}
                  className="text-accent underline-offset-4 hover:underline"
                >
                  {t("wine.takeOut")}
                </button>
                {mayWrite === true && hasShelves && (
                  <button
                    type="button"
                    onClick={() => open(group.key, "move")}
                    className="text-accent underline-offset-4 hover:underline"
                  >
                    {t("wine.move")}
                  </button>
                )}
                {/* Last, and it is the only one of the four that cannot be
                    undone by doing something ordinary afterwards — which is
                    half of why it is the owner's alone. The other half is that
                    it disposes of somebody else's property. */}
                {mayWrite === true && (
                  <button
                    type="button"
                    onClick={() => open(group.key, "depart")}
                    className="text-muted underline-offset-4 hover:underline"
                  >
                    {t("wine.depart")}
                  </button>
                )}
              </div>
            )}

            {acting?.key === group.key && acting.kind === "move" && (
              <MoveForm
                cellarId={cellarId}
                group={group}
                storages={storages}
                onCancel={() => setActing(null)}
                onDone={(said) => done(said, null)}
              />
            )}

            {acting?.key === group.key && acting.kind === "drink" && (
              <DrinkForm
                cellarId={cellarId}
                group={group}
                onCancel={() => setActing(null)}
                onDone={done}
              />
            )}

            {acting?.key === group.key && acting.kind === "away" && (
              <AwayForm
                cellarId={cellarId}
                group={group}
                onCancel={() => setActing(null)}
                onDone={done}
              />
            )}

            {acting?.key === group.key && acting.kind === "depart" && (
              <DepartForm
                cellarId={cellarId}
                group={group}
                onCancel={() => setActing(null)}
                onDone={done}
              />
            )}
          </li>
        ))}
      </ul>

      {/* Bottles that are out. Kept under their own heading rather than mixed
          into the shelves above, because they are somewhere else in the world
          and a row that implied otherwise would be the lie the status exists
          to avoid. They are still owned and still counted. */}
      {away.length > 0 && (
        <>
          <h3 className="text-muted mt-6 text-sm">{t("wine.awayHeading")}</h3>
          <ul className="mt-1 text-sm">
            {away.map((group) => (
              <li key={group.key} className="border-line border-b py-2 last:border-b-0">
                <div className="flex items-baseline justify-between gap-4">
                  <span>
                    {group.note ?? t("wine.awayNoNote")} · {t(`format.${group.format}`)}
                  </span>
                  <span className="font-display">{group.bottleIds.length}</span>
                </div>

                {/* Who carried them out, and when.

                    Drawn only when the cellar has somebody to name: the event
                    has always known, and in a cellar whose owner is the only
                    person who takes bottles out there is nothing to tell them.
                    It became worth printing when a second person could do it.

                    The day it left rather than a clock time — `occurredAt` is
                    when it happened in the world, and the minute a form was
                    submitted has no business wearing that authority. */}
                {group.by !== null && group.on !== null && (
                  <p className="text-muted mt-1">
                    {t("wine.takenOutBy", { name: group.by, day: group.on })}
                  </p>
                )}

                {/* Where it goes back to, said before the button is pressed —
                    that is what makes returning one action rather than a
                    decision. Absent when the shelf is gone, and then the form
                    below asks instead. */}
                <p className="text-muted mt-1">
                  {group.backToShelf === null
                    ? t("wine.shelfGone")
                    : t("wine.backTo", {
                        storage: group.backToStorage ?? "",
                        shelf: group.backToShelf,
                      })}
                </p>

                {/* Under the sentence rather than above it, which is the order
                    the two are read in: this is where it goes back to, and
                    this is the button that puts it there.

                    A contributor's, because carrying a bottle back is the
                    other half of carrying it out — and the person who has it
                    is the person who can say it is home. */}
                {mayCarry === true && acting?.key !== group.key && (
                  <div className="mt-1 flex flex-wrap items-baseline gap-x-5 gap-y-1">
                    {/* **Drinking a bottle that is out**, which the route has
                        allowed from the day it was written — *a bottle taken
                        to a dinner is exactly the bottle most likely to be
                        drunk there* — and which no screen offered until
                        2026-09-21.

                        What that cost was a lie in the history: the only way
                        to record it was to bring the bottle home and then
                        drink it, which writes a homecoming that never
                        happened. For a cellar whose whole argument is that the
                        append-only history is worth more than the current row,
                        that is the wrong kind of shortcut.

                        First of the two, because a bottle at a dinner is more
                        often drunk than carried back.

                        **The owner's, from the day after.** A husgäst who has
                        the bottle at a dinner is the one person who knows it
                        was drunk, and this is still not their button: what the
                        cellar holds is the owner's record, and a husgäst who
                        could empty it here would be running it. The bottle
                        stays out until the owner says otherwise, which is the
                        true state — it is not on a shelf. */}
                    {mayWrite === true && (
                      <button
                        type="button"
                        onClick={() => open(group.key, "drink")}
                        className="text-accent underline-offset-4 hover:underline"
                      >
                        {t("wine.drink")}
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => {
                        if (group.backToShelf === null) {
                          open(group.key, "return");
                          return;
                        }
                        goBack(group);
                      }}
                      className="text-accent underline-offset-4 hover:underline"
                    >
                      {t("wine.return")}
                    </button>
                  </div>
                )}

                {acting?.key === group.key && acting.kind === "drink" && (
                  <DrinkForm
                    cellarId={cellarId}
                    group={group}
                    onCancel={() => setActing(null)}
                    onDone={done}
                  />
                )}

                {acting?.key === group.key && acting.kind === "return" && (
                  <ReturnForm
                    cellarId={cellarId}
                    group={group}
                    storages={storages}
                    onCancel={() => setActing(null)}
                    onDone={(said) => done(said, null)}
                  />
                )}
              </li>
            ))}
          </ul>
        </>
      )}

      {/* The action keeps its name through the flow: Flytta ends in Flyttade,
          Drick ends in Drucken, Ge bort ends in Bortgiven, and the shelf says
          so itself if it is now fuller than it claims to hold.

          Undo sits in the confirmation rather than somewhere in a menu,
          because the moment a two-tap mistake is noticed is this one. It is
          offered after every act that takes a bottle out of the cellar and
          after none of the others: putting a bottle back is undone by taking
          it out again, which is an action that already exists. */}
      {notice !== null && (
        <p role="status" className="text-muted mt-3 flex items-baseline gap-4 text-sm">
          <span>{notice.text}</span>
          {notice.eventId !== null && (
            <button
              type="button"
              disabled={undoing}
              onClick={() => {
                const eventId = notice.eventId;
                if (eventId === null) return;
                setUndoing(true);
                void revertBottleEvent(cellarId, eventId)
                  .then((result) => {
                    // Where it actually landed, which the page cannot work out
                    // for itself: the shelf it came from may have been removed
                    // while it was gone, and then it comes back unplaced.
                    setNotice({
                      text:
                        result.status === "InCellar"
                          ? t("wine.undone", {
                              storage: result.storageName ?? "",
                              shelf: result.shelfName ?? "",
                            })
                          : t("wine.undoneUnplaced"),
                      eventId: null,
                    });
                    onMoved();
                  })
                  .catch(() => setNotice({ text: t("wine.undoFailed"), eventId }))
                  .finally(() => setUndoing(false));
              }}
              className={actionButton}
            >
              {t("wine.undo")}
            </button>
          )}
        </p>
      )}
    </>
  );
}
